import React from "react";
import { Form, Modal, message, theme, Popconfirm, Checkbox, Typography } from "antd";
import ColorButton from "../../common/ColorButton";
import { URL_SALE } from "../../constants/urls";
import { DELETE, GET, POST } from "../../frameworks/HttpClient";
import _ from "lodash";
import {
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import ErrorMessages from "../../common/ErrorMessages";
import TSTable from "../../common/TSTable";
import LoadModal from "./LoadModal";
import { dateToHumanString, dateToString, formatComma } from "../../frameworks/Util";
import { PRODUCT_TYPE, TANK_STATUS_TH } from "../../constants/string";
import { useParams } from "react-router-dom";
import LoadSkimModal from "./LoadSkimModal";
import LoadSkimProduceLotModal from "./LoadSkimProduceLotModal";
import LoadImportStockModal from "./LoadImportStockModal";
import { useUserContext } from "../../hooks/UserSettingProvider";

export default function LoadProductListModal(props) {
  const params = useParams();
  const { open, target, onUpdate, saleOrder } = props;  
  const [errorMessages, setErrorMessages] = React.useState(null)
  const [ loadTarget, setLoadTarget] = React.useState(null);
  const [ loadSkimTarget, setLoadSkimTarget] = React.useState(null);
  const [ loadSkimProduceLotTarget, setLoadSkimProduceLotTarget] = React.useState(null);
  const [total, setTotal] = React.useState({ amount: 0, weight: 0 })
  const [ loadImportStockTarget, setLoadImportStockTarget] = React.useState(null);
  const { token : { colorError, colorSuccess, }} = theme.useToken();
  const [form] = Form.useForm();
  const tableRef = React.useRef();
  const { features } = useUserContext();
  const loadImportedStockFeature = _.get(features, 'load_imported_stock_feature', false)

  const handleShowInReport = async(id) => {
    try {
      if(params.type === PRODUCT_TYPE.CONCENTRATE_LATEX){
        await POST(`${URL_SALE.LOAD_TANK_TRANSACTION}${id}/toggle-show-in-report/`)
        refreshData();
      } else {
        await POST(`${URL_SALE.LOAD_SKIM_STOCK}${id}/toggle-show-in-report/`)
        refreshData();
      }
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const fetchTotal = async() => {
    try {
      const url = params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? URL_SALE.LOAD_TANK_TRANSACTION : URL_SALE.LOAD_SKIM_STOCK
      const response = await GET(`${url}total/`, { delivery_order : target ? target.id : null })
      setTotal(_.get(response, 'data', { amount: 0, weight: 0 }))
    } catch (error) {
      setErrorMessages(error.errorMessages)
    }
  }

  const handleDelete = async (id) => {
    setErrorMessages(null);
    try {
      if(params.type === PRODUCT_TYPE.CONCENTRATE_LATEX){
        await DELETE(`${URL_SALE.LOAD_TANK_TRANSACTION}${id}/`);
        message.success("ลบรายการสำเร็จ");
        refreshData();
      } else {
        await DELETE(`${URL_SALE.LOAD_SKIM_STOCK}${id}/`);
        message.success("ลบรายการสำเร็จ");
        refreshData();
      }
    } catch (error) {
      setErrorMessages(error?.errorMessages || "เกิดข้อผิดพลาดในการลบข้อมูล");
    }
  };

  const refreshData = async() => {
    if(tableRef.current)(
      await tableRef.current.fetchWithoutResetPage()
    )
  }

  const columns = [
    {
      title : 'Action',
      dataIndex : 'id',
      key : 'Action',
      render : (value, original) => 
      <center>
        <Popconfirm
          title={"ยืนยันลบรายการโหลดหรือไม่ ?"}
          onConfirm={() => handleDelete(original.id)}
          okText={"ยืนยัน"}
          okButtonProps={{ danger: true }}
          cancelText={"ยกเลิก"}
          placement='top'>
          <ColorButton
            icon={<DeleteOutlined />}
            type={"primary"}
            override={colorError}
            onClick={() => {}}
          />
        </Popconfirm>
      </center>
    },
    {
      title : 'แสดง Lot ในรายงาน',
      dataIndex : 'show_in_report',
      key : 'show_in_report',
      render : (value, original) => 
        <center>
          <Checkbox checked={value} onChange={() => handleShowInReport(original.id)}/>
        </center>      
    },
    {
      title : `${params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? "Lot No." : "Lot No./เลขที่พาเลท"}`,
      dataIndex : 'lot_no',
      key : 'lot_no',
    },
    {
      title : 'แทงก์',
      dataIndex : 'tank_name',
      key : 'tank_name',
      hidden : (params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? false : true),
      render : (value, original) => (params.type === PRODUCT_TYPE.CONCENTRATE_LATEX && original.tank_transaction ? value : 'สต๊อกนำเข้า')
    },
    {
      title : 'สถานะ',
      dataIndex : 'tank_state',
      key : 'tank_state',
      hidden : (params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? false : true),
      render: (value) => value ? TANK_STATUS_TH[value] : "-"
    },
    
    {
      title : 'ล็อตการผลิต',
      dataIndex : 'sources_no',
      key : 'sources_no',
      hidden : (params.type === PRODUCT_TYPE.SKIM ? false : true),
    },
    {
      title : 'วันที่เริ่มผลิต',
      dataIndex : 'created',
      key : 'created',
      render: (value) => value ? dateToHumanString(value) : "-"
    },
    {
      title : `ปริมาณโหลด ${params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? "(kg)" : "(ก้อน)"}`,
      dataIndex : params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? 'weight': 'amount',
      key :  params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? 'weight': 'amount',
      render: (value) => <div style={{textAlign: "right"}}>{value ? formatComma(value) : "0"}</div>,
    },
    {
      title : 'น้ำหนักประมาณ(kg)',
      dataIndex : 'weight',
      key : 'weight',
      hidden : params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? true : false
    },
  ]

  React.useEffect(() => {
    setErrorMessages(null)
  },[open])

  return(
    <Modal
      open={open}
      onCancel={onUpdate}
      cancelText={"ยกเลิก"}
      title={params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? "รายการโหลดน้ำยางข้น" :  "รายการโหลดสินค้าสกิม"}
      width={880}
      destroyOnClose
      centered 
      footer={[
        <ColorButton onClick={onUpdate}>ปิด</ColorButton>
      ]}
    >
    <ErrorMessages message={errorMessages}/>
      <Typography.Text strong>
        รวมทั้งหมด : { params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? (
          <span>{formatComma(_.get(total, 'weight', 0))} Kgs.</span>
        ) : (
          <span>{formatComma(_.get(total, 'amount', 0))} ก้อน ({formatComma(_.get(total, 'weight', 0))} Kgs.)</span>
        )}
      </Typography.Text>

      <ColorButton 
        type='primary'
        override={colorSuccess}
        icon={<PlusOutlined style={{ fontSize : 14 }}/>}
        onClick={()=> {
          if(params.type === PRODUCT_TYPE.CONCENTRATE_LATEX){
            setLoadTarget(target)
          } else {
            setLoadSkimTarget(target)
          }
        }} 
        style={{ float : 'right', marginBottom : 10}}
      >
        {params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ? "โหลดน้ำยางข้น" : "โหลดสินค้าสกิม (พาเลท)"}
      </ColorButton>

      {params.type === PRODUCT_TYPE.CONCENTRATE_LATEX && loadImportedStockFeature && <ColorButton 
        type='primary'
        override={colorSuccess}
        icon={<PlusOutlined style={{ fontSize : 14 }}/>}
        onClick={()=> {
          setLoadImportStockTarget(target)
        }} 
        style={{ float : 'right', marginBottom : 10, marginRight : 10}}
      >
        {"โหลดน้ำยางข้นนำเข้า"}
      </ColorButton>}

      {params.type === PRODUCT_TYPE.SKIM && <ColorButton 
        type='primary'
        override={colorSuccess}
        icon={<PlusOutlined style={{ fontSize : 14 }}/>}
        onClick={()=> {
          setLoadSkimProduceLotTarget(target)
        }} 
        style={{ float : 'right', marginBottom : 10, marginRight : 10}}
      >
        {"โหลดสินค้าสกิม (ล็อตผลิต)"}
      </ColorButton>}
      
      <TSTable
        columns={columns}
        size={'small'}
        url={params.type === PRODUCT_TYPE.CONCENTRATE_LATEX ?  URL_SALE.LOAD_TANK_TRANSACTION : URL_SALE.LOAD_SKIM_STOCK }
        params={{delivery_order : target ? target.id : null}}
        ref={tableRef}
        onData={fetchTotal}
      />

      <LoadModal
        open={loadTarget ? true : false}
        onClose={() => {
          setLoadTarget(null)
        }}
        onUpdate={() => {
          setLoadTarget(null)
          refreshData()
        }}
        target={loadTarget}
        saleOrder={saleOrder}
      />

      <LoadImportStockModal
        open={loadImportStockTarget ? true : false}
        onClose={() => {
          setLoadImportStockTarget(null)
        }}
        onUpdate={() => {
          setLoadImportStockTarget(null)
          refreshData()
        }}
        target={loadImportStockTarget}
        saleOrder={saleOrder}
      />


      <LoadSkimModal
        open={loadSkimTarget ? true : false}
        onClose={() => {
          setLoadSkimTarget(null)
        }}
        onUpdate={() => {
          setLoadSkimTarget(null)
          refreshData()
        }}
        target={loadSkimTarget}
        saleOrder={saleOrder}
      />

      <LoadSkimProduceLotModal
        open={loadSkimProduceLotTarget}
        onClose={() => {
          setLoadSkimProduceLotTarget(null)
        }}
        onUpdate={() => {
          setLoadSkimProduceLotTarget(null)
          refreshData()
        }}
        target={loadSkimProduceLotTarget}
        saleOrder={saleOrder}
      />


  </Modal>)
}